/**
 * The command center: one organization's adoption and compliance health on a
 * single calm page, after Harvey's Command Center.
 *
 * Every figure comes from `/api/amoreg/command-center` (state/commandCenter),
 * computed on the server from the organization's own rows. One period picker
 * scopes every card, so the numbers always agree; while a new period loads the
 * previous answer stays on screen, dimmed. Each card can show its numbers as
 * a table and download them as CSV.
 *
 * @module components/commandCenter/CommandCenterPage
 */
import { Link } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  DownloadIcon,
  RefreshCwIcon,
  Table2Icon,
} from "lucide-react";
import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import { useStartChatWithText } from "../../hooks/useStartChatWithText";
import { cn } from "../../lib/utils";
import { orgUrlSegment } from "../../orgRouting";
import {
  useCommandCenter,
  type CommandCenterSnapshot,
  type Figure,
} from "../../state/commandCenter";
import { useActiveOrg } from "../../state/orgs";
import { AmoregMark } from "../sidebar/SidebarChrome";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { CommandCenterDatePicker } from "./CommandCenterDatePicker";
import { AreaChart, BarList, ChartPatterns, ColumnChart } from "./CommandCenterCharts";
import {
  askAmoregPrompt,
  axisTicks,
  cardTables,
  commandCenterTitle,
  csvFileName,
  deltaTone,
  formatBucket,
  formatCount,
  formatDay,
  formatDayLong,
  formatDays,
  formatDelta,
  formatGbp,
  formatPercent,
  rollingMean,
  toCsv,
  type CardKey,
  type CardTable,
} from "./commandCenterLogic";
import {
  describePeriod,
  presetPeriod,
  previousPeriodLabel,
  smoothingFor,
  todayKey,
  type CommandCenterPeriod,
} from "./commandCenterPeriod";

/**
 * The page's chart inks, mixed from the theme's own tokens: neutral steps of
 * the foreground over the card, and one attention colour from destructive.
 */
const CHART_TOKENS = {
  "--cc-ink": "color-mix(in oklab, var(--foreground) 56%, var(--card))",
  // The accent the people chart wears; themed palettes without one fall back to primary.
  "--cc-accent": "var(--brand-accent, var(--primary))",
  // The messages chart's neutral ink.
  "--cc-series": "color-mix(in oklab, var(--foreground) 78%, var(--card))",
  // The hairline series: near-full ink, so the days read at a glance.
  "--cc-bar": "color-mix(in oklab, var(--foreground) 92%, var(--card))",
  "--cc-ink-strong": "color-mix(in oklab, var(--foreground) 82%, var(--card))",
  "--cc-faint": "color-mix(in oklab, var(--foreground) 18%, var(--card))",
  "--cc-grid": "color-mix(in oklab, var(--foreground) 8%, var(--card))",
  // Toward the neutral ink, so attention reads calm next to the greys, not as an alarm.
  "--cc-attention": "color-mix(in oklab, var(--destructive) 72%, var(--cc-ink))",
} as CSSProperties;

const SEVERITY_LABEL = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
  unrated: "Unrated",
} as const;

export function CommandCenterPage() {
  const org = useActiveOrg();
  const [period, setPeriod] = useState<CommandCenterPeriod>(() => presetPeriod("6m", todayKey()));
  const { result, isRefreshing, refresh } = useCommandCenter(org?.id ?? null, period);
  const startChat = useStartChatWithText(org?.id ?? null);
  const orgSegment = org ? orgUrlSegment(org.id) : null;
  const snapshot = result?.kind === "ok" ? result.snapshot : null;

  // Writes the question into the org's new-chat draft, then opens it, so the
  // composer mounts with the text (a seed caught in flight arrived empty).
  const askAmoreg = useCallback(() => {
    if (!snapshot || !startChat) return;
    void startChat(
      askAmoregPrompt(snapshot, org?.name ?? "our organization", shownPeriod(snapshot, period)),
    );
  }, [org?.name, period, snapshot, startChat]);

  return (
    <ScrollArea className="h-full">
      <div
        data-command-center=""
        className="@container mx-auto flex w-full max-w-[1240px] flex-col gap-5 px-4 pb-16 pt-14 sm:px-8 sm:pt-8"
        style={CHART_TOKENS}
      >
        <ChartPatterns />
        <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
          <div className="min-w-0">
            <h1 className="text-balance text-[28px] font-semibold leading-tight tracking-[-0.02em] text-foreground">
              {commandCenterTitle(org?.name)}
            </h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {snapshot
                ? `Compared with the ${previousPeriodLabel(shownPeriod(snapshot, period))}`
                : "How your team uses Amoreg, and how your compliance work is moving."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <CommandCenterDatePicker period={period} onChange={setPeriod} />
            <Button size="sm" onClick={askAmoreg} disabled={!snapshot || !startChat} className="gap-2">
              <AmoregMark />
              Ask Amoreg
            </Button>
          </div>
        </header>

        {result === null ? (
          <LoadingGrid />
        ) : result.kind === "admins-only" ? (
          <Notice title="Only organization admins can see the command center">
            Ask an admin of {org?.name ?? "this organization"} for a copy of the figures, or for
            admin access.
          </Notice>
        ) : result.kind === "unavailable" ? (
          <Notice title="The command center is not available on this workspace">
            This server is not connected to the organization’s records.
          </Notice>
        ) : result.kind === "error" ? (
          <Notice
            title="The command center could not load"
            action={
              <Button size="sm" variant="outline" onClick={refresh}>
                <RefreshCwIcon aria-hidden />
                Try again
              </Button>
            }
          >
            Your figures are safe; the read did not finish. Try again in a moment.
          </Notice>
        ) : (
          <div
            aria-busy={isRefreshing}
            className={cn(
              "flex flex-col gap-5 transition-opacity duration-200",
              isRefreshing && "opacity-60",
            )}
          >
            <ActionsBanner snapshot={result.snapshot} orgSegment={orgSegment} />
            <Dashboard
              snapshot={result.snapshot}
              period={shownPeriod(result.snapshot, period)}
              orgSlug={org?.slug ?? orgSegment ?? "organization"}
              orgSegment={orgSegment}
            />
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

/**
 * The period the shown figures cover. While a newly chosen period loads, the
 * previous answer stays on screen, and its labels must describe it, not the
 * new choice.
 */
function shownPeriod(
  snapshot: CommandCenterSnapshot,
  chosen: CommandCenterPeriod,
): CommandCenterPeriod {
  return snapshot.window.since === chosen.from && snapshot.window.until === chosen.to
    ? chosen
    : { from: snapshot.window.since, to: snapshot.window.until, preset: null };
}

// ── Banner ─────────────────────────────────────────────────────────────

function ActionsBanner({
  snapshot,
  orgSegment,
}: {
  snapshot: CommandCenterSnapshot;
  orgSegment: string | null;
}) {
  const { openFindings, pendingRecordUpdates } = snapshot.actions;
  const total = openFindings + pendingRecordUpdates;
  return (
    <section
      aria-label="Recommended actions"
      className="flex flex-col gap-2 rounded-2xl border border-border/60 bg-card px-5 py-3 @[46rem]:flex-row @[46rem]:items-center @[46rem]:justify-between"
    >
      <p className="flex min-w-0 items-center gap-3 text-sm text-foreground">
        <span
          aria-hidden
          className="grid size-7 shrink-0 place-items-center rounded-lg bg-foreground p-2 text-background [&>svg]:h-3 [&>svg]:w-auto"
        >
          <AmoregMark />
        </span>
        {total === 0 ? (
          <span>Nothing is waiting on you: every finding and suggestion is decided.</span>
        ) : (
          <span className="min-w-0 @[46rem]:truncate">
            {total} recommended {total === 1 ? "action" : "actions"}
            <span className="text-muted-foreground">
              {" "}
              · {openFindings} open {openFindings === 1 ? "finding" : "findings"},{" "}
              {pendingRecordUpdates} {pendingRecordUpdates === 1 ? "suggestion" : "suggestions"} to review
            </span>
          </span>
        )}
      </p>
      {orgSegment && total > 0 && (
        <div className="-mx-2 flex shrink-0 flex-wrap items-center gap-0.5 @[46rem]:mx-0">
          {openFindings > 0 && (
            <Button
              size="sm"
              variant="ghost"
              render={<Link to="/$org/compliance/findings" params={{ org: orgSegment }} />}
            >
              Review findings
              <ArrowRightIcon aria-hidden />
            </Button>
          )}
          {pendingRecordUpdates > 0 && (
            <Button
              size="sm"
              variant="ghost"
              render={<Link to="/$org/compliance/suggestions" params={{ org: orgSegment }} />}
            >
              Review suggestions
              <ArrowRightIcon aria-hidden />
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

// ── Cards ──────────────────────────────────────────────────────────────

function Delta({
  figure,
  upIsGood = true,
  previousLabel,
}: {
  figure: Figure;
  upIsGood?: boolean;
  previousLabel: string;
}) {
  const text = formatDelta(figure.delta);
  if (text === null) {
    return <span className="text-[13px] text-muted-foreground">No earlier data</span>;
  }
  const tone = deltaTone(figure.delta, upIsGood);
  return (
    <span
      className={cn(
        "text-sm font-medium tabular-nums",
        tone === "good"
          ? "text-success"
          : tone === "bad"
            ? "text-destructive-foreground"
            : "text-muted-foreground",
      )}
      title={`${formatCount(figure.previous)} in the ${previousLabel}`}
    >
      {text}
    </span>
  );
}

function Kpi({ value, label, delta }: { value: string; label: string; delta?: ReactNode }) {
  return (
    <div>
      <div className="flex items-baseline gap-2.5">
        <span className="text-[44px] font-medium leading-none tracking-[-0.035em] text-foreground">
          {value}
        </span>
        {delta}
      </div>
      <div className="mt-2 text-[13px] text-muted-foreground">{label}</div>
    </div>
  );
}

interface LegendEntry {
  readonly label: string;
  readonly mark: "bar" | "bar-thin" | "hairline" | "line" | "dotted" | "attention" | "series";
  /** For a `series` mark: the line's colour. */
  readonly color?: string;
}

function LegendMark({ mark, color }: { mark: LegendEntry["mark"]; color?: string | undefined }) {
  if (mark === "bar-thin")
    return <span aria-hidden className="h-[3px] w-3.5 rounded-full bg-[var(--cc-faint)]" />;
  if (mark === "series")
    return (
      <span aria-hidden className="h-[2px] w-3.5 rounded-full" style={{ background: color }} />
    );
  if (mark === "line") return <span aria-hidden className="h-px w-3.5 bg-foreground/35" />;
  if (mark === "hairline")
    return <span aria-hidden className="h-2.5 w-[2px] rounded-full bg-[var(--cc-bar)]" />;
  if (mark === "dotted")
    return (
      <span
        aria-hidden
        className="size-2.5 rounded-full"
        style={{
          backgroundImage: "radial-gradient(circle, var(--cc-ink) 0.7px, transparent 0.9px)",
          backgroundSize: "3px 3px",
          backgroundColor: "color-mix(in oklab, var(--cc-faint) 45%, transparent)",
        }}
      />
    );
  return (
    <span
      aria-hidden
      className={cn(
        "size-2.5 rounded-full",
        mark === "attention" ? "bg-[var(--cc-attention)]" : "bg-[var(--cc-ink)]",
      )}
    />
  );
}

function download(name: string, table: CardTable) {
  const blob = new Blob([toCsv(table.header, table.rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function FooterButton({
  label,
  onClick,
  pressed,
  children,
}: {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="icon-sm"
            variant="ghost-muted"
            aria-label={label}
            aria-pressed={pressed}
            onClick={onClick}
          >
            {children}
          </Button>
        }
      />
      <TooltipPopup>{label}</TooltipPopup>
    </Tooltip>
  );
}

function Card({
  card,
  title,
  subtitle,
  kpi,
  legend,
  table,
  fileName,
  className,
  footerLink,
  children,
}: {
  card: CardKey;
  title?: string;
  subtitle?: string;
  kpi?: ReactNode;
  legend: ReadonlyArray<LegendEntry>;
  table: CardTable;
  fileName: string;
  className?: string;
  /** A link beside the footer's buttons, e.g. "Open Usage". */
  footerLink?: ReactNode;
  children: ReactNode;
}) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section
      aria-label={title ?? card}
      className={cn(
        "flex min-w-0 flex-col rounded-2xl border border-border/60 bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]",
        className,
      )}
    >
      <div className="flex flex-1 flex-col gap-5 px-5 pb-5 pt-5 sm:px-6 sm:pt-6">
        {kpi}
        {title && (
          <div>
            <h2 className="text-[15px] font-medium text-foreground">{title}</h2>
            {subtitle && <p className="mt-0.5 text-[13px] text-muted-foreground">{subtitle}</p>}
          </div>
        )}
        {/* A figure card keeps its chart on the floor of the card, as the reference does; a titled card reads top-down. */}
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-col",
            kpi !== undefined && "min-h-[200px] justify-end",
          )}
        >
          {asTable ? <CardTableView table={table} /> : children}
        </div>
      </div>
      <footer className="flex items-center justify-between gap-3 border-t border-border/60 px-5 py-2.5 sm:px-6">
        <ul className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
          {legend.map((entry) => (
            <li key={entry.label} className="flex items-center gap-1.5">
              <LegendMark mark={entry.mark} color={entry.color} />
              {entry.label}
            </li>
          ))}
        </ul>
        <div className="flex shrink-0 items-center gap-0.5">
          {footerLink}
          <FooterButton
            label={asTable ? "Show the chart" : "Show as a table"}
            pressed={asTable}
            onClick={() => setAsTable((value) => !value)}
          >
            <Table2Icon aria-hidden />
          </FooterButton>
          <FooterButton label="Download CSV" onClick={() => download(fileName, table)}>
            <DownloadIcon aria-hidden />
          </FooterButton>
        </div>
      </footer>
    </section>
  );
}

function CardTableView({ table }: { table: CardTable }) {
  return (
    <div className="max-h-64 overflow-auto rounded-lg border border-border/60">
      <table className="w-full text-left text-[13px]">
        <thead className="sticky top-0 bg-card text-muted-foreground">
          <tr>
            {table.header.map((cell) => (
              <th
                key={cell}
                scope="col"
                className="border-b border-border/60 px-3 py-2 font-medium"
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, index) => (
            <tr key={index} className="border-b border-border/40 last:border-0">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={cn("px-3 py-1.5", typeof cell === "number" && "tabular-nums")}
                >
                  {cell ?? "–"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Notice({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="flex flex-col items-start gap-3 rounded-2xl border border-border/60 bg-card px-6 py-8">
      <h2 className="text-[17px] font-medium text-foreground">{title}</h2>
      <p className="max-w-prose text-sm text-muted-foreground">{children}</p>
      {action}
    </section>
  );
}

function LoadingGrid() {
  return (
    <div role="status" className="grid gap-5 @[52rem]:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <span className="sr-only">Loading the command center</span>
      <div className="h-[72px] animate-pulse rounded-2xl bg-muted/60 @[52rem]:col-span-2" />
      {[5, 7, 5, 7].map((span, index) => (
        <div
          key={index}
          className={cn(
            "h-[420px] animate-pulse rounded-2xl bg-muted/60",
            span === 5 ? "min-w-0" : "min-w-0",
          )}
        />
      ))}
    </div>
  );
}

// ── The dashboard ──────────────────────────────────────────────────────

function Dashboard({
  snapshot,
  period,
  orgSlug,
  orgSegment,
}: {
  snapshot: CommandCenterSnapshot;
  period: CommandCenterPeriod;
  orgSlug: string;
  orgSegment: string | null;
}) {
  const { activity, productUsage, powerUsers, compliance, spend, window } = snapshot;
  const previousLabel = previousPeriodLabel(period);
  const tables = useMemo(() => cardTables(snapshot), [snapshot]);
  const file = (card: CardKey) => csvFileName(orgSlug, card, snapshot);
  const days = activity.daily.map((day) => day.date);
  const ticks = axisTicks(days);
  // Long periods read as a trend: averaged over a week, or a fortnight past two months.
  const smoothing = smoothingFor(days.length);
  const previousName = previousLabel.charAt(0).toUpperCase() + previousLabel.slice(1);
  const comparisonName =
    smoothing === 1 ? previousName : `${previousName}, ${smoothing}-day average`;
  const bucketLabel = (start: string) => formatBucket(start, window.granularity);
  const bucketTick = (start: string) =>
    window.granularity === "week" ? formatDay(start) : formatDayLong(start).slice(0, 3);
  const weeklyLabelEvery = window.granularity === "week" ? (compliance.flow.length > 8 ? 4 : 1) : 1;
  const memberNoun = productUsage.members === 1 ? "member" : "members";

  return (
    <div className="grid gap-5 @[52rem]:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <Card
        card="activeUsers"
        className="min-w-0"
        kpi={
          <Kpi
            value={formatCount(activity.activeUsers.value)}
            label="Active users · sent a message"
            delta={<Delta figure={activity.activeUsers} previousLabel={previousLabel} />}
          />
        }
        legend={[
          {
            label:
              smoothing > 1 ? `People active, ${smoothing}-day average` : "People active each day",
            mark: "series",
            color: "var(--cc-accent)",
          },
          { label: comparisonName, mark: "line" },
        ]}
        table={tables.activeUsers}
        fileName={file("activeUsers")}
      >
        <AreaChart
          color="var(--cc-accent)"
          labels={days.map(formatDayLong)}
          values={rollingMean(
            activity.daily.map((day) => day.activeUsers),
            smoothing,
          )}
          valuesRaw={smoothing > 1 ? activity.daily.map((day) => day.activeUsers) : undefined}
          averageLabel={smoothing > 1 ? `${smoothing}-day average` : undefined}
          comparison={rollingMean(
            activity.daily.map((day) => day.previousActiveUsers),
            smoothing,
          )}
          comparisonRaw={activity.daily.map((day) => day.previousActiveUsers)}
          ticks={ticks}
          seriesLabel="active that day"
          comparisonLabel="same day, previous period"
          format={formatCount}
          ariaLabel={`Active users per day: ${formatCount(activity.activeUsers.value)} people over ${describePeriod(period)}`}
        />
      </Card>

      <Card
        card="productUsage"
        className="min-w-0"
        title="Product usage"
        subtitle={`Share of the ${productUsage.members} ${memberNoun} who used each part of Amoreg`}
        legend={[
          { label: "This period", mark: "bar" },
          { label: previousLabel.replace(/^p/, "P"), mark: "dotted" },
          {
            label: `Needs attention · under ${formatPercent(productUsage.attentionBelow)}`,
            mark: "attention",
          },
        ]}
        table={tables.productUsage}
        fileName={file("productUsage")}
      >
        <ColumnChart
          series={[
            { key: "current", label: "this period", style: "solid" },
            { key: "previous", label: "previous period", style: "dotted" },
          ]}
          categories={productUsage.surfaces.map((surface) => ({
            key: surface.key,
            label: surface.label,
            ...(surface.users === 0 && surface.previousUsers === 0 ? { empty: "No use yet" } : {}),
            values: [
              {
                value: surface.share,
                display: formatPercent(surface.share),
                detail: `${surface.users} of ${productUsage.members}`,
                attention: surface.needsAttention,
              },
              {
                value: surface.previousShare,
                display: formatPercent(surface.previousShare),
                detail: `${surface.previousUsers} of ${productUsage.members}`,
              },
            ],
          }))}
          max={1}
          crowding="stagger"
          formatTick={formatPercent}
          ariaLabel="Product usage: share of members who used each part of Amoreg, this period and the previous one"
        />
      </Card>

      <Card
        card="messages"
        className="min-w-0"
        kpi={
          <Kpi
            value={formatCount(activity.messages.value)}
            label="Messages sent to Amoreg"
            delta={<Delta figure={activity.messages} previousLabel={previousLabel} />}
          />
        }
        legend={[
          {
            label: smoothing > 1 ? `Messages, ${smoothing}-day average` : "Messages each day",
            mark: "series",
            color: "var(--cc-series)",
          },
          { label: comparisonName, mark: "line" },
        ]}
        table={tables.messages}
        fileName={file("messages")}
      >
        <AreaChart
          color="var(--cc-series)"
          labels={days.map(formatDayLong)}
          values={rollingMean(
            activity.daily.map((day) => day.messages),
            smoothing,
          )}
          valuesRaw={smoothing > 1 ? activity.daily.map((day) => day.messages) : undefined}
          averageLabel={smoothing > 1 ? `${smoothing}-day average` : undefined}
          comparison={rollingMean(
            activity.daily.map((day) => day.previousMessages),
            smoothing,
          )}
          comparisonRaw={activity.daily.map((day) => day.previousMessages)}
          ticks={ticks}
          seriesLabel="messages that day"
          comparisonLabel="same day, previous period"
          format={formatCount}
          ariaLabel={`Messages sent per day: ${formatCount(activity.messages.value)} over ${describePeriod(period)}`}
        />
      </Card>

      <Card
        card="powerUsers"
        className="min-w-0"
        title="Top power users"
        subtitle="Days with any activity: chats, decisions, Probe, automations"
        legend={[{ label: "Active days", mark: "bar" }]}
        table={tables.powerUsers}
        fileName={file("powerUsers")}
      >
        {powerUsers.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Nobody has used Amoreg in this period yet.
          </p>
        ) : (
          <BarList
            ariaLabel="Top power users by active days"
            rows={powerUsers.map((user) => ({
              key: user.userId,
              label: user.name,
              value: user.activeDays,
              display: `${user.activeDays} ${user.activeDays === 1 ? "day" : "days"}`,
            }))}
          />
        )}
      </Card>

      <div className="flex items-baseline justify-between gap-4 pt-4 @[52rem]:col-span-2">
        <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-foreground">
          Compliance health
        </h2>
        <p className="text-[13px] text-muted-foreground">
          Findings include gaps in your document set
        </p>
      </div>

      <Card
        card="findings"
        className="min-w-0"
        kpi={
          <Kpi
            value={formatCount(compliance.openFindings)}
            label={`Open findings · ${compliance.opened.value} opened, ${compliance.resolved.value} resolved this period`}
          />
        }
        legend={[
          { label: "Opened", mark: "bar" },
          { label: "Resolved", mark: "dotted" },
        ]}
        table={tables.findings}
        fileName={file("findings")}
      >
        <ColumnChart
          series={[
            { key: "opened", label: "opened", style: "solid" },
            { key: "resolved", label: "resolved", style: "dotted" },
          ]}
          categories={compliance.flow.map((bucket) => ({
            key: bucket.start,
            label: bucketTick(bucket.start),
            title: bucketLabel(bucket.start),
            values: [
              { value: bucket.opened, display: formatCount(bucket.opened) },
              { value: bucket.resolved, display: formatCount(bucket.resolved) },
            ],
          }))}
          formatTick={formatCount}
          labelEvery={weeklyLabelEvery}
          ariaLabel={`Findings opened and resolved per ${window.granularity}`}
        />
      </Card>

      <Card
        card="severity"
        className="min-w-0"
        title="Open findings by severity"
        subtitle="High and critical findings need attention first"
        legend={[
          { label: "Open findings", mark: "bar" },
          { label: "High or critical", mark: "attention" },
        ]}
        table={tables.severity}
        fileName={file("severity")}
      >
        <BarList
          ariaLabel="Open findings by severity"
          rows={compliance.severity.map((row) => ({
            key: row.severity,
            label: SEVERITY_LABEL[row.severity],
            value: row.open,
            display: formatCount(row.open),
            attention: (row.severity === "critical" || row.severity === "high") && row.open > 0,
          }))}
        />
        <dl className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60">
          <Stat
            label="Mean time to resolve"
            value={formatDays(compliance.meanDaysToResolve.value)}
            note={
              compliance.meanDaysToResolve.count > 0
                ? `${compliance.meanDaysToResolve.count} resolved`
                : "None resolved yet"
            }
          />
          <Stat
            label="Suggestions applied"
            value={formatCount(compliance.recordUpdates.applied)}
            note={`${compliance.recordUpdates.skipped} skipped`}
          />
          <Stat
            label="Suggestions waiting"
            value={formatCount(compliance.recordUpdates.pending)}
            note="To review now"
          />
          <Stat
            label="Inspector runs"
            value={formatCount(compliance.inspectorRuns.value)}
            note={`${formatCount(compliance.inspectorRuns.previous)} in the ${previousLabel}`}
          />
        </dl>
      </Card>

      {spend && (
        <Card
          card="spend"
          className="@[52rem]:col-span-2"
          kpi={
            <Kpi
              value={formatGbp(spend.costGbp.value)}
              label={`Model spend · ${formatCount(spend.tokens.value)} tokens, priced as on the Usage page`}
              delta={
                <Delta figure={spend.costGbp} upIsGood={false} previousLabel={previousLabel} />
              }
            />
          }
          legend={[{ label: `Spend per ${window.granularity}`, mark: "bar" }]}
          footerLink={
            orgSegment ? (
              <Button
                size="sm"
                variant="ghost"
                className="me-1 text-[12px] text-muted-foreground"
                render={<Link to="/$org/usage" params={{ org: orgSegment }} />}
              >
                Open Usage
                <ArrowRightIcon aria-hidden />
              </Button>
            ) : null
          }
          table={tables.spend}
          fileName={file("spend")}
        >
          <ColumnChart
            series={[{ key: "cost", label: "spend", style: "solid" }]}
            categories={spend.flow.map((bucket) => ({
              key: bucket.start,
              label: bucketTick(bucket.start),
              title: bucketLabel(bucket.start),
              values: [
                {
                  value: bucket.costGbp,
                  display: formatGbp(bucket.costGbp),
                  detail: `${formatCount(bucket.tokens)} tokens`,
                },
              ],
            }))}
            formatTick={(value) => (value === 0 ? "£0" : formatGbp(value).replace(/\.00$/, ""))}
            labelEvery={window.granularity === "week" ? 2 : 1}
            height={220}
            ariaLabel={`Model spend per ${window.granularity}`}
          />
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="bg-card px-4 py-3.5">
      <dt className="text-[12px] text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-[20px] font-semibold leading-tight tracking-[-0.02em] text-foreground">
        {value}
      </dd>
      <dd className="mt-0.5 text-[12px] text-muted-foreground">{note}</dd>
    </div>
  );
}
